import "./AccountSearchComponent.scss";
import template from "./AccountSearchComponent.html";
import {IComponentOptions, IOnDestroy, IOnInit} from "angular";
import {AccountSource} from "../../../types/AccountSource";
import {BehaviorSubject, Observable, ReplaySubject, Scheduler, Subject} from "rxjs";
import {Consumer, Predicate, UnaryTransform} from "../../../../types/GenericTypes";
import {AccountBriefRecord, AccountBriefRecords} from "../../../types/AccountBriefRecord";
import {
    AccountSearchViewModel,
    AccountSearchViewModels,
    buildModels,
    orderModels
} from "./models/AccountSearchViewModel";
import {Optional} from "../../../../js/util/Optional";
import {LanguageService} from "../../../../translation/service/LanguageService";
import {TranslationsLanguage} from "../../../../translation/config/TranslationsLanguage";

export const AccountSearch = "tfAccountSearch";

export function accountSearch(): IComponentOptions {
    return {
        template,
        bindings: {
            source: "<",
            hideSearchIcon: "<",
            hideArrowButton: "<",
        },
        controllerAs: "ctrl",
        controller: AccountSearchComponentController
    };
}

export class AccountSearchComponentController implements IOnInit, IOnDestroy {

    /** @input */
    public set source(source: AccountSource | null) {
        if (source != null) {
            this.source$.next(source);
        }
    }

    public hideSearchIcon: boolean | null;

    public hideArrowButton: boolean | null;

    private readonly source$ = new ReplaySubject<AccountSource>();

    public searchValue: AccountSearchViewModel | string | null;
    public readonly placeholderText$ = new ReplaySubject<string>(1);
    public readonly currentId$ = new ReplaySubject<number | null>(1);

    private readonly inputFocus$ = new BehaviorSubject(false);
    private readonly models$ = new ReplaySubject<AccountSearchViewModels>(1);

    private readonly accounts$ = new BehaviorSubject<AccountBriefRecords>([]);
    private readonly current$ = new BehaviorSubject<AccountBriefRecord | null>(null);

    private readonly destroyed$ = new ReplaySubject<boolean>(1);

    /** @ngInject */
    public constructor(private readonly languageService: LanguageService) {
    }

    public $onInit(): void {
        this.source$
            .takeUntil(this.destroyed$)
            .observeOn(Scheduler.async)
            .subscribe(this.watchSource());
        this.watchModelsSource();
        this.watchSelectedSources();
        this.watchCurrent();
    }

    private watchSource(): Consumer<AccountSource> {
        const sourceChanged$ = new Subject<boolean>();
        return (source) => {
            sourceChanged$.next(true);
            source.accounts.subscribe((accounts) => this.accounts$.next(accounts));
            source.current.subscribe((current) => this.current$.next(current));
        };
    }

    private watchModelsSource() {
        this.accounts$
            .takeUntil(this.destroyed$)
            .observeOn(Scheduler.async)
            .map(buildModels)
            .subscribe((models) => this.models$.next(models));
    }

    private watchSelectedSources() {
        const source$ = Observable.combineLatest(this.currentModel(), this.inputFocus$, this.languageService.current());
        source$.takeUntil(this.destroyed$)
               .observeOn(Scheduler.async)
               .map(([model, focus, language]) => this.placeholderText(model, focus, language))
               .subscribe((placeholder) => this.placeholderText$.next(placeholder));
    }

    private currentModel(): Observable<AccountSearchViewModel | null> {
        const source$ = Observable.combineLatest(this.models$, this.current$);
        return source$.takeUntil(this.destroyed$)
                      .observeOn(Scheduler.async)
                      .map(([models, current]) => this.findCurrentModel(models, current));
    }

    private findCurrentModel(models: AccountSearchViewModels,
                             current: AccountBriefRecord | null): AccountSearchViewModel | null {
        if (current == null) {
            return null;
        } else {
            return models.find((m) => m.id === current.id)!;
        }
    }

    private placeholderText(model: AccountSearchViewModel | null, focus: boolean, language: TranslationsLanguage): string {
        if (focus) {
            return "";
        } else if (model == null) {
            return AccountSearchComponentController.defaultPlaceholder(language);
        } else {
            return model.display;
        }
    }

    private static defaultPlaceholder(language: TranslationsLanguage): string {
        switch (language) {
            case TranslationsLanguage.Chinese:
                return "搜索当前账户";
            case TranslationsLanguage.English:
                return "Search for Account";
        }
    }

    private watchCurrent() {
        this.current$
            .observeOn(Scheduler.async)
            .takeUntil(this.destroyed$)
            .map((current) => Optional.of(current).map((c) => c.id).orNull())
            .subscribe((id) => this.currentId$.next(id));
    }

    public findMatches(filter: string | null): Promise<AccountSearchViewModels> {
        return this.models$
                   .first()
                   .takeUntil(this.destroyed$)
                   .map(this.filterModels(filter))
                   .map(orderModels(filter))
                   .map((models) => this.limitResults(models))
                   .toPromise();
    }

    private filterModels(filter: string | null): UnaryTransform<AccountSearchViewModels> {
        return (accounts) => accounts.filter(this.matches(filter));
    }

    private matches(filter: string | null): Predicate<AccountSearchViewModel> {
        if (filter == null) {
            return () => true;
        } else {
            const searchValue = filter.toLocaleLowerCase();
            return (model) => model.display.toLocaleLowerCase().includes(searchValue);
        }
    }

    private limitResults(models: AccountSearchViewModels): AccountSearchViewModels {
        return models.slice(0, 20);
    }

    public focused() {
        this.inputFocus$.next(true);
    }

    public blurred() {
        this.inputFocus$.next(false);
        this.searchValue = null;
    }

    public selectAccount($item: AccountBriefRecord) {
        this.source$
            .first()
            .takeUntil(this.destroyed$)
            .subscribe((source) => source.selectAccount($item.id));
        this.searchValue = null;
    }

    public $onDestroy(): void {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }
}
